
import React, { useState } from 'react';
import { Dialog, DialogContent, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Label } from '@/components/ui/label';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { formatDocumentsForExport, downloadJsonFile } from '../utils/knowledge-import-export';
import { Checkbox } from '@/components/ui/checkbox';
import { KnowledgeDocument } from '../types';

interface ExportKnowledgeDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  documents: KnowledgeDocument[];
}

export const ExportKnowledgeDialog: React.FC<ExportKnowledgeDialogProps> = ({
  open,
  onOpenChange,
  documents
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [language, setLanguage] = useState<string>('all');
  const [topic, setTopic] = useState<string>('all');
  const [includeMetadata, setIncludeMetadata] = useState(true);
  const [includeVectors, setIncludeVectors] = useState(false);

  const handleExport = async () => {
    try {
      setIsLoading(true);
      toast.info('Preparing documents for export...');
      
      // Use provided documents instead of fetching
      let filteredData = documents;
      
      // Apply filters to provided documents
      if (language !== 'all') {
        filteredData = filteredData.filter(doc => doc.metadata?.language === language);
      }
      
      if (topic !== 'all') {
        filteredData = filteredData.filter(doc => doc.metadata?.topic === topic);
      }
      
      if (filteredData.length === 0) {
        toast.warning('No documents found matching your criteria');
        return;
      }
      
      // Format data for export
      let exportData = formatDocumentsForExport(filteredData);
      
      // Remove vectors if not including them
      if (!includeVectors) {
        exportData = exportData.map((doc: any) => {
          const { vector, ...rest } = doc;
          return rest;
        });
      }
      
      // Generate and download file
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      const filename = `knowledge-export-${timestamp}.json`;
      
      downloadJsonFile(exportData, filename);
      
      toast.success(`Successfully exported ${filteredData.length} documents`);
      onOpenChange(false);
    } catch (error) {
      console.error('Export error:', error);
      toast.error('Failed to export documents');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(newOpen) => {
      if (!isLoading) {
        onOpenChange(newOpen);
      }
    }}>
      <DialogContent className="max-w-md">
        <DialogHeader>
          <DialogTitle>Export Knowledge Documents</DialogTitle>
        </DialogHeader>
        
        <div className="space-y-4 py-4">
          <div className="grid grid-cols-2 gap-4">
            <div className="space-y-2">
              <Label htmlFor="language-filter">Language</Label>
              <Select value={language} onValueChange={setLanguage}>
                <SelectTrigger id="language-filter">
                  <SelectValue placeholder="Select language" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Languages</SelectItem>
                  <SelectItem value="en">English</SelectItem>
                  <SelectItem value="vi">Vietnamese</SelectItem>
                  <SelectItem value="zh">Chinese</SelectItem>
                  <SelectItem value="ja">Japanese</SelectItem>
                  <SelectItem value="ko">Korean</SelectItem>
                  <SelectItem value="th">Thai</SelectItem>
                </SelectContent>
              </Select>
            </div>
            
            <div className="space-y-2">
              <Label htmlFor="topic-filter">Topic</Label>
              <Select value={topic} onValueChange={setTopic}>
                <SelectTrigger id="topic-filter">
                  <SelectValue placeholder="Select topic" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Topics</SelectItem>
                  <SelectItem value="guide">Guide</SelectItem>
                  <SelectItem value="performance">Performance</SelectItem>
                  <SelectItem value="trading_strategy">Trading Strategy</SelectItem>
                  <SelectItem value="advisor">Advisor</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-4">
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="include-metadata" 
                checked={includeMetadata} 
                onCheckedChange={(checked) => setIncludeMetadata(checked as boolean)}
              />
              <Label htmlFor="include-metadata" className="cursor-pointer">Include metadata</Label>
            </div>
            
            <div className="flex items-center space-x-2">
              <Checkbox 
                id="include-vectors" 
                checked={includeVectors} 
                onCheckedChange={(checked) => setIncludeVectors(checked as boolean)}
              />
              <Label htmlFor="include-vectors" className="cursor-pointer">Include vector embeddings</Label>
            </div>
          </div>
        </div>
        
        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={isLoading}>
            Cancel
          </Button>
          <Button onClick={handleExport} disabled={isLoading}>
            {isLoading ? 'Exporting...' : 'Export Documents'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};
